import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail, API } from "@/lib/api";
import { ArrowLeft, Upload, Save, Trash2, Building2, Plus, X, UserCircle2, FileArchive, Download, Package, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "",
  nif: "",
  email: "",
  phone: "",
  address: { street: "", number: "", postal_code: "", city: "", country: "Portugal" },
  logo_path: null,
  notes: "",
  contacts: [],
  logo_assets: [],
  newsletter_opt_in: false,
};

const emptyContact = () => ({ name: "", role: "", email: "", phone: "" });

const ClientForm = () => {
  const { id } = useParams();
  const isNew = !id || id === "novo";
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!isNew) {
      (async () => {
        try {
          const { data } = await api.get(`/clients/${id}`);
          setForm({
            ...empty,
            ...data,
            address: { ...empty.address, ...(data.address || {}) },
            contacts: data.contacts || [],
            logo_assets: data.logo_assets || [],
          });
        } catch (e) {
          toast.error("Cliente não encontrado");
          navigate("/clientes");
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [id, isNew, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));

  const addContact = () =>
    setForm((f) => ({ ...f, contacts: [...(f.contacts || []), emptyContact()] }));
  const updateContact = (idx, k, v) =>
    setForm((f) => ({
      ...f,
      contacts: f.contacts.map((c, i) => (i === idx ? { ...c, [k]: v } : c)),
    }));
  const removeContact = (idx) =>
    setForm((f) => ({ ...f, contacts: f.contacts.filter((_, i) => i !== idx) }));

  const moveContact = (idx, direction) => {
    setForm((f) => {
      const arr = [...(f.contacts || [])];
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= arr.length) return f;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return { ...f, contacts: arr };
    });
  };

  const assetRef = useRef(null);
  const [uploadingAsset, setUploadingAsset] = useState(false);

  const uploadAssets = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingAsset(true);
    try {
      const newAssets = [];
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads/asset", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newAssets.push(data);
      }
      setForm((f) => ({ ...f, logo_assets: [...(f.logo_assets || []), ...newAssets] }));
      toast.success(`${newAssets.length} arquivo(s) enviado(s)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao enviar arquivo");
    } finally {
      setUploadingAsset(false);
    }
  };

  const removeAsset = (idx) =>
    setForm((f) => ({ ...f, logo_assets: f.logo_assets.filter((_, i) => i !== idx) }));

  const assetDownloadUrl = (assetPath) => {
    const token = localStorage.getItem("bm_token") || "";
    return `${API}/files/${assetPath}?download=1&auth=${encodeURIComponent(token)}`;
  };

  const humanSize = (n) => {
    if (!n) return "";
    const kb = n / 1024;
    if (kb < 1024) return `${kb.toFixed(0)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("logo_path", data.path);
      toast.success("Logo enviado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha no upload");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/clients", form);
        toast.success("Cliente criado");
        navigate(`/clientes/${data.id}`);
      } else {
        await api.put(`/clients/${id}`, form);
        toast.success("Cliente atualizado");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm("Apagar este cliente?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Cliente apagado");
    navigate("/clientes");
  };

  if (loading) return <div className="text-zinc-500">Carregando...</div>;

  return (
    <div className="max-w-4xl mx-auto fade-up" data-testid="client-form-page">
      <button
        onClick={() => navigate("/clientes")}
        className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-300 mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-2">
          {isNew ? "Novo Registro" : "Editar"}
        </div>
        <h1 className="font-display text-4xl font-black text-zinc-50 tracking-tighter">
          {isNew ? "Cadastrar Cliente" : form.name}
        </h1>
      </div>

      <form onSubmit={save} className="space-y-8">
        {/* Logo + Identificação */}
        <section className="rounded-xl border border-zinc-800 bg-[#121214] p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-4">Identificação</div>
          <div className="grid md:grid-cols-[180px,1fr] gap-6">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">Logo</label>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="client-logo-upload-btn"
                className="w-full aspect-square rounded-lg border border-dashed border-zinc-700 bg-[#0e0e10] flex items-center justify-center overflow-hidden hover:border-zinc-500 transition-all"
              >
                {form.logo_path ? (
                  <img src={fileUrl(form.logo_path)} alt="" className="w-full h-full object-contain" />
                ) : (
                  <div className="flex flex-col items-center text-zinc-600">
                    <Upload className="w-5 h-5 mb-1" strokeWidth={1.5} />
                    <span className="text-xs">Enviar logo</span>
                  </div>
                )}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadLogo(e.target.files?.[0])}
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Nome / Razão Social *" testid="client-name-input">
                <input required value={form.name} onChange={(e) => update("name", e.target.value)} className={input} />
              </Field>
              <Field label="NIF" testid="client-nif-input">
                <input value={form.nif} onChange={(e) => update("nif", e.target.value)} className={input} placeholder="PT123456789" />
              </Field>
              <Field label="E-mail" testid="client-email-input">
                <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={input} />
              </Field>
              <Field label="Telefone" testid="client-phone-input">
                <input value={form.phone} onChange={(e) => update("phone", e.target.value)} className={input} />
              </Field>
            </div>
            <label
              className="md:col-start-2 flex items-center gap-3 p-3 rounded-lg border border-zinc-800 bg-[#0e0e10] cursor-pointer hover:border-zinc-700 transition-colors"
              data-testid="client-newsletter-optin"
            >
              <input
                type="checkbox"
                checked={!!form.newsletter_opt_in}
                onChange={(e) => update("newsletter_opt_in", e.target.checked)}
                className="w-4 h-4 accent-emerald-500"
                data-testid="client-newsletter-checkbox"
              />
              <span className="text-sm text-zinc-300">
                Inscrever na newsletter (sincroniza com o Resend)
              </span>
            </label>
          </div>
        </section>

        {/* Endereço Europeu */}
        <section className="rounded-xl border border-zinc-800 bg-[#121214] p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-4">Endereço</div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Rua / Avenida">
              <input value={form.address.street} onChange={(e) => updateAddr("street", e.target.value)} className={input} />
            </Field>
            <Field label="Número / Andar">
              <input value={form.address.number} onChange={(e) => updateAddr("number", e.target.value)} className={input} />
            </Field>
            <Field label="Código Postal">
              <input value={form.address.postal_code} onChange={(e) => updateAddr("postal_code", e.target.value)} className={input} placeholder="1000-001" />
            </Field>
            <Field label="Cidade / Localidade">
              <input value={form.address.city} onChange={(e) => updateAddr("city", e.target.value)} className={input} />
            </Field>
            <Field label="País">
              <input value={form.address.country} onChange={(e) => updateAddr("country", e.target.value)} className={input} />
            </Field>
          </div>
        </section>

        <section className="rounded-xl border border-zinc-800 bg-[#121214] p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Pessoas de Contato</div>
              <div className="text-xs text-zinc-400">Adicione múltiplas pessoas dentro desta empresa. Você poderá selecionar uma delas ao criar uma cotação.</div>
            </div>
            <button
              type="button"
              onClick={addContact}
              data-testid="add-contact-btn"
              className="flex items-center gap-2 px-3 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-lg text-xs font-medium transition-all shrink-0"
            >
              <Plus className="w-3.5 h-3.5" /> Adicionar
            </button>
          </div>

          {(form.contacts || []).length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-800 p-6 text-center" data-testid="contacts-empty">
              <UserCircle2 className="w-7 h-7 text-zinc-700 mx-auto mb-2" strokeWidth={1.5} />
              <div className="text-xs text-zinc-500">Nenhum contato cadastrado para esta empresa.</div>
            </div>
          ) : (
            <div className="space-y-3">
              {form.contacts.map((c, idx) => (
                <div
                  key={c.id || idx}
                  data-testid={`contact-row-${idx}`}
                  className="rounded-lg border border-zinc-800 bg-[#0e0e10] p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">
                      <UserCircle2 className="w-3.5 h-3.5" /> Contato {String(idx + 1).padStart(2, "0")}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => moveContact(idx, -1)}
                        disabled={idx === 0}
                        data-testid={`move-contact-up-${idx}`}
                        title="Mover para cima"
                        className="w-7 h-7 flex items-center justify-center text-zinc-500 hover:text-zinc-100 hover:bg-zinc-900 rounded transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                      >
                        <ChevronUp className="w-4 h-4" strokeWidth={2} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveContact(idx, 1)}
                        disabled={idx === form.contacts.length - 1}
                        data-testid={`move-contact-down-${idx}`}
                        title="Mover para baixo"
                        className="w-7 h-7 flex items-center justify-center text-zinc-500 hover:text-zinc-100 hover:bg-zinc-900 rounded transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                      >
                        <ChevronDown className="w-4 h-4" strokeWidth={2} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeContact(idx)}
                        data-testid={`remove-contact-${idx}`}
                        title="Remover"
                        className="w-7 h-7 flex items-center justify-center text-zinc-500 hover:text-red-400 rounded transition-colors ml-1"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">Nome *</label>
                      <input required value={c.name} onChange={(e) => updateContact(idx, "name", e.target.value)} className={input} placeholder="Ex: Maria Silva" data-testid={`contact-name-${idx}`} />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">Cargo</label>
                      <input value={c.role} onChange={(e) => updateContact(idx, "role", e.target.value)} className={input} placeholder="Ex: Gestão de Marketing" data-testid={`contact-role-${idx}`} />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">E-mail</label>
                      <input type="email" value={c.email} onChange={(e) => updateContact(idx, "email", e.target.value)} className={input} placeholder="maria@empresa.pt" data-testid={`contact-email-${idx}`} />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">Telefone</label>
                      <input value={c.phone} onChange={(e) => updateContact(idx, "phone", e.target.value)} className={input} placeholder="+351 ..." data-testid={`contact-phone-${idx}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-xl border border-zinc-800 bg-[#121214] p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <FileArchive className="w-3.5 h-3.5 text-zinc-500" strokeWidth={1.5} />
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
                  Arquivo de Logos (uso interno)
                </div>
              </div>
              <div className="text-xs text-zinc-400">
                Guarde logos em formatos vetoriais e alta resolução (EPS, AI, PDF, SVG, PNG, etc). Estes arquivos são apenas para consulta interna e <span className="text-zinc-300">nunca aparecem no PDF enviado ao cliente</span>.
              </div>
            </div>
            <button
              type="button"
              onClick={() => assetRef.current?.click()}
              disabled={uploadingAsset}
              data-testid="upload-logo-asset-btn"
              className="flex items-center gap-2 px-3 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-lg text-xs font-medium transition-all shrink-0 disabled:opacity-50"
            >
              <Upload className="w-3.5 h-3.5" /> {uploadingAsset ? "Enviando..." : "Enviar"}
            </button>
            <input
              ref={assetRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => uploadAssets(e.target.files)}
            />
          </div>

          {(form.logo_assets || []).length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-800 p-6 text-center" data-testid="logo-assets-empty">
              <FileArchive className="w-7 h-7 text-zinc-700 mx-auto mb-2" strokeWidth={1.5} />
              <div className="text-xs text-zinc-500">Nenhum arquivo de logo cadastrado.</div>
            </div>
          ) : (
            <div className="space-y-2">
              {form.logo_assets.map((a, idx) => (
                <div
                  key={a.id || a.path}
                  data-testid={`logo-asset-${idx}`}
                  className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-[#0e0e10] px-3 py-2.5"
                >
                  <div className="w-8 h-8 rounded bg-zinc-900 border border-zinc-800 flex items-center justify-center shrink-0">
                    <FileArchive className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-100 truncate font-medium">{a.filename}</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                      {(a.filename?.split('.').pop() || 'FILE').toUpperCase()}
                      {a.size ? ` · ${humanSize(a.size)}` : ""}
                    </div>
                  </div>
                  <a
                    href={assetDownloadUrl(a.path)}
                    data-testid={`download-asset-${idx}`}
                    className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-100 rounded transition-colors"
                    title="Baixar"
                  >
                    <Download className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                  <button
                    type="button"
                    onClick={() => removeAsset(idx)}
                    data-testid={`remove-asset-${idx}`}
                    className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-red-400 rounded transition-colors"
                    title="Remover"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-xl border border-zinc-800 bg-[#121214] p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-4">Notas internas</div>
          <textarea
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            rows={3}
            className={input + " resize-none"}
            placeholder="Informações úteis sobre este cliente..."
          />
        </section>

        <div className="flex items-center justify-between flex-wrap gap-3">
          {!isNew && (
            <button
              type="button"
              onClick={remove}
              data-testid="client-delete-btn"
              className="flex items-center gap-2 px-4 py-2.5 text-red-400 border border-red-900/60 hover:bg-red-950/30 rounded-lg text-sm transition-all"
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Apagar
            </button>
          )}
          <button
            type="submit"
            disabled={saving}
            data-testid="client-save-btn"
            className="ml-auto flex items-center gap-2 px-5 py-3 bg-zinc-100 hover:bg-white text-zinc-900 font-bold rounded-lg text-sm transition-all disabled:opacity-60"
          >
            <Save className="w-4 h-4" strokeWidth={2} /> {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </form>

      {!isNew && <ClientHistory id={id} />}
    </div>
  );
};

const input = "w-full px-3 py-2.5 bg-[#09090b] border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-600 focus:border-zinc-500 focus:outline-none text-sm transition-all";

const Field = ({ label, children, testid }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2" data-testid={testid}>
      {label}
    </label>
    {children}
  </div>
);

const ClientHistory = ({ id }) => {
  const [quotes, setQuotes] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/clients/${id}/quotes`);
        setQuotes(data);
      } catch {}
    })();
  }, [id]);
  return (
    <section className="mt-10">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-3">Histórico Comercial</div>
      <h2 className="font-display text-2xl font-bold text-zinc-100 mb-5">Cotações deste cliente</h2>
      {quotes.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-800 p-8 text-center text-zinc-500" data-testid="client-history-empty">
          Sem cotações para este cliente.
        </div>
      ) : (
        <div className="space-y-2">
          {quotes.map((q) => (
            <div
              key={q.id}
              onClick={() => navigate(`/cotacoes/${q.id}`)}
              data-testid={`client-history-quote-${q.id}`}
              className="rounded-lg border border-zinc-800 bg-[#121214] p-4 flex items-center justify-between gap-4 cursor-pointer hover:border-zinc-600 transition-all"
            >
              <div>
                <div className="font-mono text-zinc-200 font-semibold text-sm">{q.number}</div>
                <div className="text-xs text-zinc-500">{q.items?.length || 0} item(s) · {new Date(q.created_at).toLocaleDateString("pt-BR")}</div>
              </div>
              <div className="text-xs text-zinc-400 font-medium">{q.status}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ClientForm;
